<template>
  <v-container fluid class="group-list-container pa-0">
    <div class="view-controls mb-3">
      <div class="view-tabs">
        <v-btn-toggle v-model="viewMode" mandatory>
          <v-btn flat value="table">
            <v-icon left>view_list</v-icon>
            {{ $i18n.t("Table") }}
          </v-btn>
          <v-btn flat value="hierarchy">
            <v-icon left>account_tree</v-icon>
            {{ $i18n.t("Hierarchy") }}
          </v-btn>
        </v-btn-toggle>
      </div>
      <div class="search-container">
        <v-text-field
          v-model="search"
          prepend-inner-icon="search"
          :label="$i18n.t('Search groups')"
          single-line
          hide-details
          outlined
          dense
          class="search-field"
        ></v-text-field>
      </div>
      <div class="action-buttons">
        <v-btn color="primary" @click="openCreateDialog" class="create-btn">
          <v-icon left small>add</v-icon>
          {{ $i18n.t("New Group") }}
        </v-btn>
      </div>
    </div>

    <!-- Table View -->
    <v-card v-if="viewMode === 'table'" class="group-list-card">
      <v-data-table
        :headers="headers"
        :items="groups"
        :search="search"
        :loading="isLoading"
        :no-data-text="$i18n.t('No groups found')"
        :loading-text="$i18n.t('Loading groups...')"
        :rows-per-page-items="[10, 25, 50, 100]"
        :rows-per-page-text="$i18n.t('Rows per page:')"
        class="group-table elevation-0"
      >
        <template slot="items" slot-scope="props">
          <td>{{ props.item.shortName }}</td>
          <td>{{ props.item.fullName || "-" }}</td>
          <td>{{ getClientName(props.item.client) || "-" }}</td>
          <td>{{ getContractName(props.item.contract) || "-" }}</td>
          <td>
            <div class="d-flex align-center">
              <v-chip
                v-if="props.item.type"
                small
                :color="getTypeColor(props.item.type)"
                text-color="white"
                class="type-badge"
              >
                {{ $i18n.t(capitalize(props.item.type)) }}
              </v-chip>
              <span v-else class="no-type">-</span>
            </div>
          </td>
          <td>
            <span v-if="props.item.parent">
              {{ getParentName(props.item.parent) }}
            </span>
            <span v-else class="grey--text">{{ $i18n.t("None") }}</span>
          </td>
          <td>
            <div class="d-flex align-center">
              <v-icon small class="mr-1">people</v-icon>
              {{ props.item.users ? props.item.users.length : 0 }}
            </div>
          </td>
          <td class="actions-cell m-auto">
            <v-tooltip bottom>
              <template slot="activator">
                <v-btn icon small @click="viewGroup(props.item)" class="action-btn">
                  <v-icon small>visibility</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.t("View") }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template slot="activator">
                <v-btn icon small @click="editGroup(props.item)" class="action-btn">
                  <v-icon small>edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.t("Edit") }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template slot="activator">
                <v-btn icon small @click="manageUsers(props.item)" class="action-btn">
                  <v-icon small>person_add</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.t("Manage Users") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template slot="activator">
                <v-btn icon small @click="deleteGroup(props.item)" class="action-btn">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.t("Remove") }}</span>
            </v-tooltip>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <!-- Hierarchy View -->
    <group-hierarchy-view
      v-else
      :search="search"
      @view="viewGroup"
      @edit="editGroup"
      @manage-users="manageUsers"
      @create="openCreateDialogWithType"
    />

    <!-- Create/Edit Group Dialog -->
    <group-form
      v-if="formDialog"
      :dialog="formDialog"
      :edit-mode="editMode"
      :group="currentGroup"
      @close="formDialog = false"
      @saved="handleGroupSaved"
    />

    <!-- View Group Details Dialog -->
    <group-details
      v-if="detailsDialog"
      :dialog="detailsDialog"
      :group="currentGroup"
      @close="detailsDialog = false"
      @edit="editGroup"
    />

    <!-- Manage Users Dialog -->
    <group-users
      v-if="usersDialog"
      :dialog="usersDialog"
      :group="currentGroup"
      @close="usersDialog = false"
      @users-updated="handleUsersUpdated"
    />

    <confirm-dialog
      v-if="confirmDialog"
      :group="currentGroup"
      :children="getGroupChildren(currentGroup)"
      :dialog="confirmDialog"
      @group-delete="handleDeleteGroup"
      @close="confirmDialog = false"
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import GroupForm from "@/components/group/GroupForm.vue";
import GroupDetails from "@/components/group/GroupDetails.vue";
import GroupUsers from "@/components/group/GroupUsers.vue";
import GroupHierarchyView from "@/components/group/GroupHierarchyView.vue";
import ConfirmDialog from "@/components/group/ConfirmDialog.vue";
import { GROUP_TYPE_COLOR } from "../../../constants";

export default {
  name: "GroupList",
  components: {
    GroupForm,
    GroupDetails,
    GroupUsers,
    GroupHierarchyView,
    ConfirmDialog
  },
  data() {
    return {
      search: "",
      formDialog: false,
      detailsDialog: false,
      usersDialog: false,
      confirmDialog: false,
      editMode: false,
      currentGroup: null,
      viewMode: "table",
      headers: [
        {
          text: this.$i18n.t("Short Name"),
          align: "left",
          value: "shortName",
          sortable: true
        },
        {
          text: this.$i18n.t("Full Name"),
          value: "fullName",
          sortable: true
        },
        {
          text: this.$i18n.t("Client"),
          value: "client",
          sortable: true
        },
        {
          text: this.$i18n.t("Contract"),
          value: "contract",
          sortable: true
        },
        {
          text: this.$i18n.t("Type"),
          value: "type",
          sortable: true
        },
        {
          text: this.$i18n.t("Parent"),
          value: "parent",
          sortable: false
        },
        {
          text: this.$i18n.t("Users"),
          value: "users",
          sortable: false
        },
        {
          text: this.$i18n.t("Actions"),
          value: "actions",
          sortable: false,
          align: "center"
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      groups: "group/getGroups",
      isLoading: "group/isLoading",
      error: "group/getError",
      clients: "client/getClients",
      contracts: "contract/getContractsMeta"
    })
  },
  created() {
    this.fetchGroups();
    this.$store.dispatch("client/fetchClients");
    this.$store.dispatch("contract/fetchContractsMeta");
  },
  methods: {
    fetchGroups() {
      this.$store.dispatch("group/fetchGroups");
    },
    getGroupChildren(group) {
      return this.groups.filter(g => g.parent === group._id);
    },
    openCreateDialog() {
      this.currentGroup = {
        shortName: "",
        fullName: "",
        type: "",
        address: "",
        description: "",
        parent: null,
        manager: null,
        users: []
      };
      this.editMode = false;
      this.formDialog = true;
    },
    editGroup(group) {
      this.currentGroup = { ...group };
      this.editMode = true;
      this.formDialog = true;
      this.detailsDialog = false; // Close details dialog if open
    },
    viewGroup(group) {
      this.currentGroup = group;
      this.detailsDialog = true;
    },
    manageUsers(group) {
      this.currentGroup = group;
      this.usersDialog = true;
    },
    deleteGroup(group) {
      this.currentGroup = group;
      this.confirmDialog = true;
    },
    handleGroupSaved() {
      this.formDialog = false;
      this.fetchGroups();

      // Show success notification
      this.$store.dispatch("ui/displaySnackbar", {
        message: this.editMode
          ? this.$i18n.t("Group updated successfully")
          : this.$i18n.t("Group created successfully"),
        color: "success",
        role: "status"
      });
    },
    handleUsersUpdated() {
      this.usersDialog = false;
      this.fetchGroups();

      // Show success notification
      this.$store.dispatch("ui/displaySnackbar", {
        message: this.$i18n.t("Group users updated successfully"),
        color: "success",
        role: "status"
      });
    },
    handleDeleteGroup() {
      this.confirmDialog = false;

      this.$store.dispatch("ui/displaySnackbar", {
        message: this.$i18n.t("Group users deleted successfully"),
        color: "success",
        role: "status"
      });
    },
    getTypeColor(type) {
      return GROUP_TYPE_COLOR[type] || "grey";
    },
    getParentName(parentId) {
      const parent = this.groups.find(group => group._id === parentId);
      return parent ? parent.fullName || parent.shortName : parentId;
    },
    getClientName(clientId) {
      const client = this.clients.find(cl => cl._id === clientId);
      return client ? client.name : "-";
    },
    getContractName(contractId) {
      const contract = this.contracts.find(cn => cn._id === contractId);
      return contract ? contract.name : "-";
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric"
      }).format(date);
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    openCreateDialogWithType(type) {
      this.currentGroup = {
        shortName: "",
        fullName: "",
        type: type,
        address: "",
        description: "",
        parent: null,
        manager: null,
        users: []
      };
      this.editMode = false;
      this.formDialog = true;
    }
  }
};
</script>

<style scoped>
.group-list-container {
  margin-top: 16px;
}

.view-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 8px 16px;
}

.search-field {
  width: 300px;
}

.create-btn {
  height: 36px;
  text-transform: none;
  font-weight: normal;
}

.group-list-card {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.group-table {
  width: 100%;
}

.status-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-department {
  background-color: #ff9800;
}

.status-team {
  background-color: #4caf50;
}

.status-division {
  background-color: #2196f3;
}

.status-branch {
  background-color: #9c27b0;
}

.status-default {
  background-color: #9e9e9e;
}

.action-btn {
  margin: 0 2px;
}

/* Focus styles for accessibility */
.v-btn:focus {
  outline: 1px solid rgba(0, 0, 0, 0.2);
  outline-offset: 1px;
}
</style>
